import { catchError, finalize, map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Premises, PremisesPostTarget } from './../../model/premises';
import { RentalObjectService } from './../base/rental-object';
import { Article } from '../../model/article';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Observable } from 'rxjs';
import { RentalObjectType } from '../../model/rental-object-type';
import { IPostTargetLoader } from '../base/post-target-loader.interface';
import { FormOfLease } from '../../model/const/enums';
import { LocalDate } from '../../../core/model/local-date';
@Injectable()
export class PremisesService extends RentalObjectService<Premises> implements IPostTargetLoader<PremisesPostTarget> {
  private readonly _default_FilterFields: string[] = [
    'companyIds',
    'propertyIds',
    'buildingIds',
    'entranceIds',
    'rentalObjectType',
    'rentalId',
    'street',
  ];

  constructor(
    httpClient: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(httpClient, config, messageService, Premises, progressBar, 'premises');
  }

  getFilterDefinition(): Observable<{
    company: string;
    companyIds: string[];
    property: string;
    propertyIds: string[];
    selectedFields: string[];
    building: string;
    buildingIds: string[];
    entrance: string;
    entranceIds: string[];
    rentalObjectType: string;
    rentalObjectTypes: RentalObjectType[];
    rentalId: string;
    street: string;
    article: string;
    articles: Article[];
    formOfLease: number;
    roundRent: boolean;
    householdElectricityPayment: number;
    heatingPayment: number;
    coldWaterPayment: number;
    hotWaterPayment: number;
    defaultFields: string[];
    lettingFeatureFilters: { lettingFeatureId: string; referenceType: number | null; matchAnyReferenceType: boolean }[];
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    propertyCustomPropertiesTextValues: { key: string; value: string }[];
    propertyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    propertyCustomPropertiesNumericValues: { key: string; value: string }[];
    propertyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    buildingCustomPropertiesTextValues: { key: string; value: string }[];
    buildingCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    buildingCustomPropertiesNumericValues: { key: string; value: string }[];
    buildingCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: res.company ? res.company : null,
          companyIds: res.companyIds || [],
          property: res.property ? res.property : null,
          propertyIds: res.propertyIds || [],
          building: res.building ? res.building : null,
          buildingIds: res.buildingIds || [],
          entrance: res.entrance ? res.entrance : null,
          entranceIds: res.entranceIds || [],
          rentalObjectType: res.rentalObjectType ? res.rentalObjectType : '',
          rentalObjectTypes: Array.isArray(res.rentalObjectTypes)
            ? res.rentalObjectTypes.map(x => Object.assign(new RentalObjectType(), x))
            : [],
          rentalId: res.rentalId ? res.rentalId : '',
          street: res.street ? res.street : '',
          article: res.article || '',
          articles: Array.isArray(res.articles) ? res.articles.map(article => Object.assign(new Article(), article)) : [],
          formOfLease: res.formOfLease || null,
          roundRent: res.roundRent ?? true,
          householdElectricityPayment: res.householdElectricityPayment ?? null,
          heatingPayment: res.heatingPayment ?? null,
          coldWaterPayment: res.coldWaterPayment ?? null,
          hotWaterPayment: res.hotWaterPayment ?? null,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._default_FilterFields,
          defaultFields: this._default_FilterFields,
          lettingFeatureFilters: Array.isArray(res.lettingFeatureFilters) ? res.lettingFeatureFilters : [],
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],

          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          propertyCustomPropertiesTextValues: Array.isArray(res.propertyCustomPropertiesTextValues)
            ? res.propertyCustomPropertiesTextValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesNumericValues: Array.isArray(res.propertyCustomPropertiesNumericValues)
            ? res.propertyCustomPropertiesNumericValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesDateValues: Array.isArray(res.propertyCustomPropertiesDateValues)
            ? res.propertyCustomPropertiesDateValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          propertyCustomPropertiesBooleanValues: Array.isArray(res.propertyCustomPropertiesBooleanValues)
            ? res.propertyCustomPropertiesBooleanValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          buildingCustomPropertiesTextValues: Array.isArray(res.buildingCustomPropertiesTextValues)
            ? res.buildingCustomPropertiesTextValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          buildingCustomPropertiesNumericValues: Array.isArray(res.buildingCustomPropertiesNumericValues)
            ? res.buildingCustomPropertiesNumericValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          buildingCustomPropertiesDateValues: Array.isArray(res.buildingCustomPropertiesDateValues)
            ? res.buildingCustomPropertiesDateValues.map(v => ({ key: v.key, value: v.value }))
            : [],
          buildingCustomPropertiesBooleanValues: Array.isArray(res.buildingCustomPropertiesBooleanValues)
            ? res.buildingCustomPropertiesBooleanValues.map(v => ({ key: v.key, value: v.value }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getValidFormsOfLease(companyId: string): Observable<FormOfLease[]> {
    return this.client.get<FormOfLease[]>(`${this.endpoint}/formsOfLease/${companyId}`);
  }

  export(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<File> {
    return super.exportDataItemsFiltered(filter, includedIds, excludedIds, affectedItemsCount, 'Hyresobjekt.xlsx', true);
  }

  getPostTargetModels(buildingId: string): Observable<PremisesPostTarget[]> {
    this.progressBar.start();
    return this.client.get<PremisesPostTarget[]>(`${this.endpoint}/post-target/${buildingId}`).pipe(
      map(response => response.map(x => new PremisesPostTarget(x))),
      finalize(() => this.progressBar.stop())
    );
  }
}
